import axios, { AxiosResponse } from 'axios';

import Pagination, {
  initPagination,
  SourcePagination,
} from 'sources/sourceTypes/Pagination';
import { notifyError } from 'utils/honeyBadger';
import { toSnakeCase } from 'utils/object';
import { getDomain, getToken, getRole } from 'utils/auth';

const DEFAULT_API_DOMAIN = 'https://api.upperhand.io';

export type AVAILABLE_COLLECTION_TYPES =
  | 'addresses'
  | 'auth'
  | 'clients'
  | 'customers'
  | 'events'
  | 'staff'
  | 'sessions'
  | 'resources';

type Headers = {
  'X-Jwt-Token': string | null;
  'X-Customer-Id': string | undefined;
};

export default abstract class BaseSource<T, SourceType, ParamsType> {
  static checkIsAuthorized(responseStatus?: number): boolean {
    return !(responseStatus === 401 || responseStatus === 403);
  }

  abstract ObjectURL: AVAILABLE_COLLECTION_TYPES;

  abstract toSource: (obj: T) => SourceType;

  abstract toType: (obj: SourceType) => T;

  // eslint-disable-next-line class-methods-use-this
  get baseUrl(): string {
    const domain = getDomain();

    if (!domain) return DEFAULT_API_DOMAIN;

    if (domain.includes('.')) {
      const trailingBackSlashRegEx = /\/$/;
      const cleanDomain = domain.replace(trailingBackSlashRegEx, '');
      const hasProtocol = /^https?:\/\//.test(cleanDomain);

      return hasProtocol ? cleanDomain : `https://${cleanDomain}`;
    }

    switch (domain) {
      case 'dev':
        return 'http://localhost:3000';
      case 'upperhand':
        return DEFAULT_API_DOMAIN;
      default:
        return `https://${domain}-api.upperhand.io`;
    }
  }

  get Url(): string {
    return `${this.baseUrl}/api/${this.ObjectURL}`;
  }

  async Post(obj: T): Promise<T> {
    try {
      const response = await axios.post<SourceType>(
        this.Url,
        obj,
        this.headers
      );

      return this.toType(response.data);
    } catch (error) {
      const isAuthorized = BaseSource.checkIsAuthorized(error.response?.status);
      this.notifyError('Post error', error);

      if (!isAuthorized) this.logoutWithRedirect();

      throw Error(error.message);
    }
  }

  async Fetch(id: string): Promise<T> {
    try {
      const response = await axios.get<SourceType>(
        `${this.Url}/${id}`,
        this.headers
      );

      return this.toType(response.data);
    } catch (error) {
      const isAuthorized = BaseSource.checkIsAuthorized(error.response?.status);
      this.notifyError('Fetch error', error);

      if (!isAuthorized) this.logoutWithRedirect();

      throw Error(error.message);
    }
  }

  async List(params?: ParamsType): Promise<Pagination<T>> {
    try {
      const snakedParams =
        params && toSnakeCase(initPagination<ParamsType>(params));

      const response = await axios.get<
        SourcePagination<SourceType, AVAILABLE_COLLECTION_TYPES>
      >(this.Url, {
        params: snakedParams,
        ...this.headers,
      });

      const {
        page,
        per_page: perPage,
        total_count: totalCount,
      } = response.data;

      return {
        page,
        perPage,
        totalCount,
        records: response.data[this.ObjectURL].map(d => this.toType(d)),
      };
    } catch (error) {
      const isAuthorized = BaseSource.checkIsAuthorized(error.response?.status);
      this.notifyError('List error', error);

      if (!isAuthorized) this.logoutWithRedirect();

      throw Error(error.message);
    }
  }

  async ListAll(params?: ParamsType): Promise<Array<T>> {
    try {
      const result: Array<T> = [];

      // Fetch and store first page to figure out how many left
      const response = await this.getPage(params);
      const { per_page: perPage, total_count: totalCount } = response.data;
      result.push(...this.sourceResponseToTypedCollection(response));

      // Determine how many pages left and prepare requests
      const pages = Math.ceil(totalCount / perPage);

      const responses: Array<Promise<
        AxiosResponse<SourcePagination<SourceType, AVAILABLE_COLLECTION_TYPES>>
      >> = [];

      for (let i = 1; i < pages; i += 1) {
        responses.push(this.getPage(params, i + 1));
      }

      // Execute all requests and store results
      const completeResponse = await Promise.all(responses);

      completeResponse.forEach(resp => {
        result.push(...this.sourceResponseToTypedCollection(resp));
      });

      return result;
    } catch (error) {
      const isAuthorized = BaseSource.checkIsAuthorized(error.response?.status);
      this.notifyError('List error', error);

      if (!isAuthorized) this.logoutWithRedirect();

      throw Error(error.message);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  protected get headers(): { headers: Headers } {
    return {
      headers: {
        'X-Jwt-Token': getToken(),
        'X-Customer-Id': getRole()?.customerId,
      },
    };
  }

  protected notifyError = notifyError('ApiCall', {
    source: this.constructor.name,
    domain: getDomain(),
  });

  /* eslint-disable class-methods-use-this */
  private logoutWithRedirect(): void {
    window.localStorage.clear();
    window.location.replace('/login');
  }
  /* eslint-enable class-methods-use-this */

  private getPage(
    params?: ParamsType,
    page = 1
  ): Promise<
    AxiosResponse<SourcePagination<SourceType, AVAILABLE_COLLECTION_TYPES>>
  > {
    const snakedParams =
      params &&
      toSnakeCase(
        initPagination<ParamsType>({ ...params, page })
      );

    return axios.get<SourcePagination<SourceType, AVAILABLE_COLLECTION_TYPES>>(
      this.Url,
      {
        params: snakedParams,
        ...this.headers,
      }
    );
  }

  private sourceResponseToTypedCollection(
    response: AxiosResponse<
      SourcePagination<SourceType, AVAILABLE_COLLECTION_TYPES>
    >
  ): Array<T> {
    const responseData = response.data;
    return responseData[this.ObjectURL].map(item => this.toType(item));
  }
}
